import { appConstants } from "../../appConstants"
import { WithId } from "../../type/WithId"

export type ValidationRule<T> = {
  fieldName: keyof T
  errorMessage: string
  rule(form: T): boolean
}

export function required<T>(fieldName: keyof T): ValidationRule<T> {
  return {
    fieldName,
    rule: (form: T) => form[fieldName] !== undefined && form[fieldName] !== null && form[fieldName] !== "",
    errorMessage: "Ce champ est obligatoire",
  }
}

export function requiredSelect<T>(fieldName: keyof T): ValidationRule<T> {
  return {
    fieldName,
    rule: (form) => {
      const value: WithId = form[fieldName] as WithId
      return !!value?.id && value.id !== "empty"
    },
    errorMessage: "Ce champ est obligatoire",
  }
}

export function nonZero<T>(fieldName: keyof T): ValidationRule<T> {
  return {
    fieldName,
    rule: (form: T) => form[fieldName] !== 0,
    errorMessage: "Ce champ ne doit pas être égal à zéro",
  }
}

export function email<T>(fieldName: keyof T): ValidationRule<T> {
  return {
    fieldName,
    rule: (form: T) => {
      const value = form[fieldName]
      const isString = !!value && typeof value === "string"
      if (isString) {
        return appConstants.regex.email.test(value)
      } else {
        return false
      }
    },
    errorMessage: "Le format de l'email est incorrect",
  }
}

export function phone<T>(fieldName: keyof T, isRequired: boolean): ValidationRule<T> {
  return {
    fieldName,
    rule: (form: T) => {
      const value = form[fieldName]
      if (!isRequired && !value) {
        // If not required, empty field is ok
        return true
      } else {
        const isString = !!value && typeof value === "string"
        if (isString) {
          const phoneNumber = value
          if (phoneNumber.startsWith("+") && (phoneNumber.length === 12 || phoneNumber.length === 13)) {
            return appConstants.regex.onlyDigits.test(phoneNumber.substring(1))
          } else {
            return phoneNumber.length === 10 && appConstants.regex.onlyDigits.test(phoneNumber)
          }
        } else {
          return false
        }
      }
    },
    errorMessage: "Le format du numéro de téléphone est incorrect",
  }
}

export function zipCode<T>(fieldName: keyof T): ValidationRule<T> {
  return {
    fieldName,
    rule: (form: any) => appConstants.regex.zipCode.test(form[fieldName]),
    errorMessage: "Ce champ est obligatoire",
  }
}

export function requiredAtLeastOne<T>(fieldNames: (keyof T)[]): ValidationRule<T>[] {
  return fieldNames.map((field) => ({
    fieldName: field,
    rule: (form: T) => fieldNames.some((f) => !!form[f]),
    errorMessage: "Vous devez sélectionner au moins une option.",
  }))
}

export function nonZerodAtLeastOne<T>(fieldNames: (keyof T)[]): ValidationRule<T>[] {
  return fieldNames.map((field) => ({
    fieldName: field,
    rule: (form: T) => fieldNames.some((f) => form[f] !== 0),
    errorMessage: "Vous devez remplir au moins une option.",
  }))
}

export function nonSuperior<T>(fieldName: keyof T, maxValue: number): ValidationRule<T> {
  return {
    fieldName,
    rule: (form: T) => {
      const value = form[fieldName]
      return typeof value === "number" && value <= maxValue
    },
    errorMessage: `Ce champ ne doit pas être supérieur à ${maxValue}`,
  }
}

export function sumEqualTo100<T>(fieldName1: keyof T, fieldName2: keyof T): ValidationRule<T> {
  return {
    fieldName: fieldName1,
    rule: (form: T) => {
      const value1 = form[fieldName1] as number
      const value2 = form[fieldName2] as number
      return value1 + value2 === 100
    },
    errorMessage: "La somme des pourcentages cloisonné et Open space doit être égale à 100%",
  }
}

export function validateNameToDelete(
  nameToDelete: string | undefined,
  nameFromUser: string | undefined
): { isValid: boolean; newError: Record<string, string> } {
  const newError: Record<string, string> = {}
  let isValid = true

  if (nameToDelete === undefined) {
    newError.nameToDelete = "Il n'y a pas de sélection à supprimer"
    isValid = false
  } else if (nameFromUser === undefined) {
    newError.nameToDelete = "Veuillez remplir ce champ"
    isValid = false
  } else {
    const nameValue = nameToDelete?.trim()

    if (!nameValue?.length) {
      newError.nameToDelete = "Veuillez remplir ce champ"
      isValid = false
    }

    if (nameValue !== nameFromUser) {
      newError.nameToDelete = "Le nom est incorrect"
      isValid = false
    }

    if (nameValue.length > 1000) {
      newError.nameToDelete = "1000 caractères maximum"
      isValid = false
    }
  }

  return { isValid, newError }
}
